import { FC, useEffect } from 'react';
import { useAppSelector } from 'helpers/hooks';
import { IEvent } from 'store/eventPage/types';
import { IPage } from 'store/donatePage/types';
import theme from 'config/theme';
import { useNavigate, useParams } from 'react-router-dom';
import { clearCheckoutIdempotencyKey } from 'services/storage';
import { getPageImageUrl } from 'helpers/imageUrls';
import { PledgeConfirmation as PledgeConfirmationComponent } from '../shared/PledgeConfirmation';
import { getCompletedTransactions } from 'selectors/pay';
import { TransactionResponse } from 'store/pay/types';
import Loader from 'components/Loader';
import { getUrlSearchParamsStrNoMethod } from 'helpers/utils';

const getDate = () => {
  const dateObj = new Date();
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getTime = () => {
  const dateObj = new Date();
  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getDonationAmount = (completedTransactions: TransactionResponse[]) => {
  return completedTransactions.reduce((acc, transaction) => acc + transaction.amount, 0);
};

interface Props {
  page?: IPage | IEvent;
}

export const PledgeConfirmation: FC<Props> = ({ page }) => {
  const navigate = useNavigate();
  const { urlPartner } = useParams();
  const { completedTransactions } = useAppSelector(state => ({
    completedTransactions: getCompletedTransactions(state),
  }));

  useEffect(() => {
    clearCheckoutIdempotencyKey();
    if (!completedTransactions.length) {
      navigate(`/donate?${getUrlSearchParamsStrNoMethod()}`);
    }
  }, []);

  if (!completedTransactions.length) {
    return (
      <div className="confirmation">
        <div className="loader-container">
          <Loader width={60} height={60} />
        </div>
      </div>
    );
  }

  const isEvent = !!page?.event_id;
  const textColor = isEvent ? 'white' : 'inkBlue';
  const headerBackground = isEvent ? theme.colors.oathBlue : undefined;
  const date = getDate();
  const time = getTime();
  const pageImage = urlPartner && page ? getPageImageUrl(page) : '';

  return (
    <PledgeConfirmationComponent
      // Every row of one wire donation shares this id (op_<uuid5 of the idempotency key>).
      conversionId={completedTransactions[0]?.external_donation_id}
      donationAmount={getDonationAmount(completedTransactions)}
      page={page}
      date={date}
      headerBackground={headerBackground}
      isEvent={isEvent}
      pageImage={pageImage}
      recipientNames={completedTransactions.map(transaction => transaction.recipient)}
      textColor={textColor}
      time={time}
    />
  );
};
